import React from 'react';
import type { AdminPreviewBlock, AdminPreviewKind } from '../../data/marco2';

/**
 * Static, logic-free miniature of an Admin screen's layout: sidebar, top bar and
 * content blocks. Pure markup — no Admin shell, data or permission logic loads.
 */
export function AdminThumbnail({
  title,
  blocks
}: {
  title: string;
  blocks: AdminPreviewBlock[];
}) {
  return <div className="relative aspect-[16/10.5] w-full overflow-hidden bg-sand-deep" aria-hidden="true" data-id="element-976" data-mp="2s8p842gxfy1h">
      <div className="absolute inset-[12px] flex overflow-hidden rounded-[10px] border border-sand-line bg-sand shadow-card" data-id="element-977" data-mp="2n92wtyknsrvb">
        <div className="flex w-[19%] shrink-0 flex-col gap-[5px] bg-deep px-[6px] py-[8px]" data-id="element-978" data-mp="2dgx3akere2e0">
          <span className="mb-[3px] h-[7px] w-[7px] rounded-pill bg-gold" data-id="element-979" data-mp="28in5e21ngnop" />
          {[70, 55, 80, 60, 65, 50, 72, 58].map((width, index) => <span key={index} className={`block h-[3px] rounded-pill ${index === 0 ? 'bg-gold' : 'bg-sand/25'}`} style={{
          width: `${width}%`
        }} data-id="element-980" data-mp="24g8qtu50ka38" data-mpi={index} />)}
        </div>
        <div className="flex min-w-0 flex-1 flex-col" data-id="element-981" data-mp="3ncxkuxuvsy24">
          <div className="flex h-[16px] shrink-0 items-center gap-[6px] border-b border-sand-line bg-white px-[8px]" data-id="element-982" data-mp="3s6ijms4atw2d">
            <span className="truncate text-[7px] font-bold leading-none text-deep" data-id="element-983" data-mp="3efn6v4qoiuti">{title}</span>
            <span className="ml-auto h-[6px] w-[34px] rounded-pill bg-sand-deep" data-id="element-984" data-mp="3ig7408b95yfn" />
            <span className="h-[7px] w-[7px] rounded-pill bg-soft" data-id="element-985" data-mp="a15m7d5tf0t3" />
          </div>
          <div className="grid flex-1 auto-rows-min grid-cols-2 gap-[5px] overflow-hidden p-[7px]" data-id="element-986" data-mp="ez76g1ez9665">
            {blocks.map((block, index) => <div key={`${block.kind}-${index}`} className={block.wide ? 'col-span-2' : ''} data-id="element-987" data-mp="11a6qw588jn9" data-mpi={index}>
                <Block kind={block.kind} data-id="element-988" data-mp="5wft1w2jk0bv" />
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
function Line({
  width,
  tone = 'bg-deep/15'
}: {
  width: string;
  tone?: string;
}) {
  return <span className={`block h-[3px] rounded-pill ${tone}`} style={{
    width
  }} data-id="element-989" data-mp="2qm56c4nlje2g" />;
}
function Panel({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="rounded-[6px] border border-sand-line bg-white p-[5px]" data-id="element-990" data-mp="ra7xtgskq29q">{children}</div>;
}
const statusTones = ['bg-soft/30', 'bg-gold/40', 'bg-soft/30', 'bg-deep/15', 'bg-soft/30'];
function Block({
  kind
}: {
  kind: AdminPreviewKind;
}) {
  switch (kind) {
    case 'kpis':
      return <div className="grid grid-cols-4 gap-[4px]" data-id="element-991" data-mp="dit9cufwd8va">
          {[0, 1, 2, 3].map(item => <Panel key={item} data-id="element-992" data-mp="8hng8vibrjl8">
              <Line width="60%" data-id="element-993" data-mp="8hdqn916zeg2" />
              <span className={`mt-[4px] block h-[6px] w-[45%] rounded-pill ${item === 1 ? 'bg-gold' : 'bg-deep/60'}`} data-id="element-994" data-mp="3jc76mzutd42" />
            </Panel>)}
        </div>;
    case 'line':
      return <Panel data-id="element-995" data-mp="1xxd432bufq1i">
          <Line width="35%" tone="bg-deep/30" data-id="element-996" data-mp="1xxwpit0pcwka" />
          <svg viewBox="0 0 100 30" className="mt-[4px] block h-[34px] w-full" preserveAspectRatio="none" data-id="element-997" data-mp="1xvitohy77gbo">
            <polyline points="0,24 14,20 28,22 42,14 56,16 70,9 84,12 100,6" fill="none" stroke="var(--bee-soft)" strokeWidth="2" vectorEffect="non-scaling-stroke" data-id="element-998" data-mp="1tu72i7zoen1d" />
            <polyline points="0,27 14,25 28,26 42,21 56,22 70,18 84,19 100,15" fill="none" stroke="var(--bee-gold)" strokeWidth="2" vectorEffect="non-scaling-stroke" data-id="element-999" data-mp="3grg8sm612r91" />
          </svg>
        </Panel>;
    case 'bars':
      return <Panel data-id="element-1000" data-mp="3pmw3f5elxafo">
          <Line width="40%" tone="bg-deep/30" data-id="element-1001" data-mp="2sv277ovsw5pb" />
          <div className="mt-[4px] flex flex-col gap-[4px]" data-id="element-1002" data-mp="2swdbrqx2lhhh">
            {[80, 62, 48, 35].map((width, index) => <div key={index} className="flex items-center gap-[4px]" data-id="element-1003" data-mp="2xvzolz8zkvpr" data-mpi={index}>
                <Line width="22%" data-id="element-1004" data-mp="31up6a4124zf7" />
                <span className="block h-[4px] rounded-pill bg-soft/70" style={{
              width: `${width * 0.7}%`
            }} data-id="element-1005" data-mp="31xtjfqwi35i0" />
              </div>)}
          </div>
        </Panel>;
    case 'table':
      return <Panel data-id="element-1006" data-mp="m42q8tpqqnm1">
          <div className="flex gap-[6px] border-b border-sand-line pb-[4px]" data-id="element-1007" data-mp="m3t79q345gpu">
            {[18, 22, 14, 16].map((width, index) => <Line key={index} width={`${width}%`} tone="bg-deep/30" data-id="element-1008" data-mp="knp76qnhwqu6" data-mpi={index} />)}
          </div>
          {statusTones.map((tone, index) => <div key={index} className="flex items-center gap-[5px] border-b border-sand-line py-[4px] last:border-0" data-id="element-1009" data-mp="tnlt4kii3blj" data-mpi={index}>
              <span className="h-[6px] w-[6px] shrink-0 rounded-pill bg-soft/60" data-id="element-1010" data-mp="pm0gn09vozbv" />
              <Line width="26%" tone="bg-deep/25" data-id="element-1011" data-mp="13li0d18bhmed" />
              <Line width="20%" data-id="element-1012" data-mp="13k6y55uismpd" />
              <span className={`ml-auto h-[5px] w-[18px] rounded-pill ${tone}`} data-id="element-1013" data-mp="ymyfz59dgio4" />
            </div>)}
        </Panel>;
    case 'tabs':
      return <div className="flex gap-[8px] border-b border-sand-line pb-[3px]" data-id="element-1014" data-mp="1db1iu5s4be8p">
          {[0, 1, 2, 3].map(item => <span key={item} className="flex flex-col gap-[2px]" data-id="element-1015" data-mp="18f44g50f0hyk">
              <span className={`block h-[3px] w-[24px] rounded-pill ${item === 0 ? 'bg-deep/60' : 'bg-deep/20'}`} data-id="element-1016" data-mp="1md2ewzbm7u8s" />
              {item === 0 && <span className="block h-[1.5px] w-[24px] bg-gold" data-id="element-1017" data-mp="1hblhjdscapwy" />}
            </span>)}
        </div>;
    case 'filters':
      return <div className="flex items-center gap-[4px]" data-id="element-1018" data-mp="25wzxr52r4hi0">
          <span className="h-[10px] w-[38%] rounded-pill border border-sand-line bg-white" data-id="element-1019" data-mp="1ryjrff8swt0e" />
          {[0, 1, 2].map(item => <span key={item} className={`h-[8px] w-[14%] rounded-pill ${item === 0 ? 'bg-deep' : 'border border-deep/20'}`} data-id="element-1020" data-mp="1vzdde32onxqn" />)}
        </div>;
    case 'matrix':
      return <Panel data-id="element-1021" data-mp="1mzqcrjhs2vyn">
          <div className="grid gap-[3px]" style={{
          gridTemplateColumns: '2fr repeat(6, minmax(0, 1fr))'
        }} data-id="element-1022" data-mp="1n08ir7u8sojr">
            {Array.from({
            length: 5
          }, (_, row) => <React.Fragment key={row} data-id="element-1023" data-mp="1d9dszthsf6tk">
                <Line width="80%" data-id="element-1024" data-mp="1daf4fz2ptvbt" />
                {Array.from({
              length: 6
            }, (_, col) => <span key={col} className={`mx-auto h-[5px] w-[5px] rounded-[1.5px] ${col === 5 ? 'border border-dashed border-gold' : (row + col) % 3 === 0 ? 'border border-deep/25' : 'bg-deep/60'}`} data-id="element-1025" data-mp="zb72wzj2yb4g" />)}
              </React.Fragment>)}
          </div>
        </Panel>;
    case 'form':
      return <Panel data-id="element-1026" data-mp="2w5yzlerjspvt">
          {[0, 1, 2].map(item => <div key={item} className="mb-[4px] last:mb-0" data-id="element-1027" data-mp="2s81aebxs23sf">
              <Line width="35%" data-id="element-1028" data-mp="3owqri01kysdc" />
              <span className="mt-[2px] block h-[8px] rounded-[3px] border border-sand-line" data-id="element-1029" data-mp="3ovxrdhx4n146" />
            </div>)}
        </Panel>;
    case 'rule':
      return <div className="rounded-[6px] bg-deep p-[6px]" data-id="element-1030" data-mp="3fvsvo51apr5u">
          <Line width="40%" tone="bg-sand/30" data-id="element-1031" data-mp="3fyf4s93u4eq6" />
          <span className="mt-[5px] block text-[9px] font-bold leading-none text-gold" data-id="element-1032" data-mp="3ay0v6mml4wq1">35 ml × kg</span>
          <span className="mt-[5px] inline-block h-[5px] w-[22px] rounded-pill bg-soft" data-id="element-1033" data-mp="24i6z3yt3wnaa" />
        </div>;
    case 'cards':
      return <div className="grid grid-cols-3 gap-[4px]" data-id="element-1034" data-mp="1zk5hzjf8qy1g">
          {[0, 1, 2].map(item => <Panel key={item} data-id="element-1035" data-mp="exxc6v7ylyb4">
              <span className="block h-[7px] w-[7px] rounded-[2px] bg-sand-deep" data-id="element-1036" data-mp="jyv4w1qliaqv" />
              <span className="mt-[4px] block" data-id="element-1037" data-mp="jwqpamjc3nar"><Line width="70%" tone="bg-deep/30" data-id="element-1038" data-mp="o0gdhaw3k2v9" /></span>
              <span className="mt-[3px] block" data-id="element-1039" data-mp="nyvijk5vehwv"><Line width="50%" data-id="element-1040" data-mp="sryf815y27ha" /></span>
            </Panel>)}
        </div>;
    case 'donut':
      return <Panel data-id="element-1041" data-mp="xpq5kqqxxljd">
          <div className="flex items-center gap-[6px]" data-id="element-1042" data-mp="xq008rmxkgve">
            <svg viewBox="0 0 40 40" className="h-[38px] w-[38px] shrink-0" data-id="element-1043" data-mp="12p31e0zsi2y3">
              <circle cx="20" cy="20" r="14" fill="none" stroke="var(--bee-soft)" strokeWidth="6" strokeDasharray="40 88" transform="rotate(-90 20 20)" data-id="element-1044" data-mp="17opfsdrctp8h" />
              <circle cx="20" cy="20" r="14" fill="none" stroke="var(--bee-gold)" strokeWidth="6" strokeDasharray="22 88" strokeDashoffset="-40" transform="rotate(-90 20 20)" data-id="element-1045" data-mp="1bpr6253amqyb" />
              <circle cx="20" cy="20" r="14" fill="none" stroke="var(--bee-line)" strokeWidth="6" strokeDasharray="26 88" strokeDashoffset="-62" transform="rotate(-90 20 20)" data-id="element-1046" data-mp="3dgvyv4gypxk8" />
            </svg>
            <div className="flex flex-1 flex-col gap-[3px]" data-id="element-1047" data-mp="2pn3y7bkvtep6">
              <Line width="70%" data-id="element-1048" data-mp="2pkhop6efkp4b" />
              <Line width="55%" data-id="element-1049" data-mp="33dyyhzhbenh5" />
              <Line width="40%" data-id="element-1050" data-mp="33d7g5qcqam8t" />
            </div>
          </div>
        </Panel>;
    case 'log':
      return <Panel data-id="element-1051" data-mp="2ze8ag3j6algb">
          {[0, 1, 2, 3, 4].map(item => <div key={item} className="flex items-center gap-[5px] border-b border-sand-line py-[4px] last:border-0" data-id="element-1052" data-mp="2bncceoo4iu0m">
              <Line width="12%" data-id="element-1053" data-mp="26pau45yiuyq7" />
              <span className={`h-[5px] w-[5px] shrink-0 rounded-pill ${item === 1 ? 'bg-gold' : 'bg-soft/60'}`} data-id="element-1054" data-mp="2kn7u7cc6pn9m" />
              <Line width="30%" tone="bg-deep/25" data-id="element-1055" data-mp="2fnmu2yla38vb" />
              <Line width="18%" data-id="element-1056" data-mp="2fmtklpuxq59h" />
            </div>)}
        </Panel>;
    default:
      return null;
  }
}